import type { AuditCapacity, LifecycleFaultSummary } from './auditCapacity';
import type { OtaProposePayload, OtaPublishResult } from './ota-release';
import type { KnowledgeStatus, PoolRow, PoolsView, ProjectInput, ProjectView, ProjectsView, RemovalRow, ReportView, ScopeText, SourcesPage } from './knowledge';
import type { HistoryStorageState } from './historyStorage';
import type { BackgroundErasureReview, BackgroundErasureSubmission, EraseResult, LabelPolicy, LabelPolicyStatus, OfficeInstall } from './governance';
import type { PppAdminPatch, PppAdminView } from './ppp';
import type { StarscribeAdminPatch, StarscribeAdminStatus, StarscribeAdminView } from './starscribe';
import type {
  PulseDigest, PulseDigestListItem, PulseDomainScorecard, PulseFeedbackRow, PulseIntentDetail, PulseIntentRow, PulseList, PulseOperations, PulseOverview,
  PulseProblem, PulseProblemStatus, PulseRecord, PulseSettingsPatch, PulseSettingsView, PulseSubjectAction, PulseSubjectRow,
} from './pulse';
import { pulseQuery } from './pulse';
import type { PeopleSearch } from './people';
import { usageQueryString, type UsageQuery } from './usage';
import type { CirclesBody, CirclesView, DesktopPolicyBody, DesktopPolicyView, DevicePolicyRow, ResolvedPerson } from './pcSwitches';
import type {
  AddDayOutfitBody,
  AddMascotOutfitBody,
  DesktopMascotAdded,
  DesktopMascotCatalog,
  RestoreOutfitBody,
  UpdateDayBody,
  UpdateMascotOutfitBody,
} from './desktopMascot';
import type { ReferenceSlot, ReferenceView, ReplaceReferenceBody, RollbackReferenceBody } from './mascotReference';
import type { AssignCategoryBody, AssignCategoryResult, CategoriesView, SaveCategoriesBody } from './outfitCategories';
import type { FullAccessView, SaveFullAccessBody } from './fullOutfitAccess';
import type { CelebrationsView, SaveCelebrationsBody } from './celebrations';
import type { McpReadOnlyServer, McpReadOnlyView } from './mcpReadOnly';
import type { PetModerationPackage, PetModerationInput, PetReviewQueue, PetSupportStatus, PetOffboardingReview, PetOffboardingReceipt } from './pets';

import type { BackfillAnswer, BackfillScope, FacingAction, FacingActionAnswer, FacingSummary } from './outfitFacing';
import type { ReplacePictureAnswer, ReplacePictureBody } from './outfitPicture';
import type { EconomyView, SaveEconomyCollectionBody, SaveEconomyItemBody, SaveEconomyPackBody, SupplyReport } from './outfitEconomy';
export type { AuditCapacity, LifecycleFaultSummary } from './auditCapacity';
export type { OtaProposePayload, OtaPublishResult, OtaRelease } from './ota-release';
export type { HistoryStorageState } from './historyStorage';

/**
 * The fetch options for a console API call: the session cookie always, and a JSON content-type only when the call has a
 * body (as GyroClient.headers(withBody) does). gyro-svc runs Fastify 5, which parses a DELETE like a POST: a body-less
 * DELETE or POST that declares application/json is refused with 400 FST_ERR_CTP_EMPTY_JSON_BODY before the route runs,
 * which used to break Revoke, webhook delete, erase and sign-out. A content-type the caller sets is kept.
 */
export function requestInit(init: RequestInit = {}): RequestInit {
  const { headers, ...rest } = init;
  const merged = new Headers(headers);
  if (rest.body !== undefined && rest.body !== null && !merged.has('content-type')) merged.set('content-type', 'application/json');
  return { credentials: 'include', ...rest, headers: merged };
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, requestInit(init));
  if (res.status === 401) throw new ApiError('unauthenticated', 401);
  if (res.status === 403) throw new ApiError('forbidden', 403);
  if (!res.ok) {
    // Surface the service's structured error body (e.g. the 422 consent-gate refusal) to callers.
    const body = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
    const message = typeof body?.message === 'string' ? body.message : typeof body?.error === 'string' ? body.error : `request failed: ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: Record<string, unknown>) {
    super(message);
  }
}

/** A Pulse list's query (lib/pulse.ts pulseQuery): `days`, `domain`, `surface`, `q`, `sort`, `cursor`, `limit` and a route's own filters. */
export type PulseQueryParams = Record<string, string | number | boolean | null | undefined>;

export interface AdminUser {
  id: string;
  displayName: string;
  email: string;
  isAdmin: boolean;
  /** Read-only auditor (optional AUDITOR_GROUP_ID): may view admin GET surfaces, never mutate. */
  isAuditor?: boolean;
}

export interface VoiceSettings {
  speed: number;
  stability: number;
  similarity: number;
  style: number;
}

/** The effect on Gyro's spoken voice (gyro-core voiceEffects.ts). */
export type VoiceEffect = 'none' | 'light-robot';

export interface BudgetSettings {
  dailyUsd?: number;
  monthlyUsd?: number;
  ratePerMin?: number;
  burst?: number;
}

export interface UserCaps {
  dailyUsd?: number;
  weeklyUsd?: number;
  monthlyUsd?: number;
  dailyTokens?: number;
  weeklyTokens?: number;
  monthlyTokens?: number;
}

/**
 * PUT /v1/admin/usage-caps. Each change carries what the screen loaded, and the service writes nothing when that is no
 * longer what is stored (409 user_caps_changed), so a stale screen never overwrites a newer cap (#233).
 */
export interface UserCapsChange {
  /** The new default; {} or null clears it. */
  userCapsDefault?: UserCaps | null;
  /** The default the form was built from ({} when there was none); required with userCapsDefault. */
  expectedDefault?: UserCaps;
  /** One person's caps (null removes the override), with the override the screen loaded for them (null when none). */
  setUser?: { email: string; caps: UserCaps | null; expected: UserCaps | null };
}

export interface UserSpendRow {
  email: string;
  dayTokens: number;
  weekTokens: number;
  monthTokens: number;
  dayUsd: number;
  weekUsd: number;
  monthUsd: number;
}

/** One answer or usage limit (mirrors gyro-svc usageLimits.UsageLimitRow), set with PUT /v1/admin/config {limits}. */
export interface UsageLimitRow {
  key: string;
  /** The field of `limits` that saves it. */
  setting: string;
  /** The release value, which applies when the console sets nothing. */
  default: number | Record<string, number>;
  /** The console set a value that differs from the release value. */
  overridden: boolean;
  /** What may be saved (whole numbers). */
  bounds: { min: number; max: number };
  /** What the limit is, in plain words ("Longest answer per model call"). */
  name: string;
  /** One number, or one number per reasoning effort (or per voice level). */
  value: number | Record<string, number>;
  unit: 'tokens' | 'rounds' | 'calls' | 'turns' | 'searches';
  meaning: string;
  source: string;
}

export interface UsageCapsStatus {
  /** Fleet-default per-user cap (PUT /v1/admin/usage-caps {userCapsDefault}). */
  default: UserCaps;
  /** Per-user overrides keyed by lowercased email (PUT /v1/admin/usage-caps {setUser}). */
  perUser: Record<string, UserCaps>;
  spend: UserSpendRow[];
  /** The four global caps, as stored; a field is absent when not set (PUT /v1/admin/config {budget}). */
  budget: BudgetSettings;
  /** What the rate fields fall back to when not set. Spend caps have no fallback: not set = no cap. */
  budgetDefaults: { ratePerMin: number; burst: number };
  /** The answer and usage limits in force, with their release values and bounds (PUT /v1/admin/config {limits}). */
  limits: UsageLimitRow[];
  /** The thinking depth people pick for each reasoning effort (low = Fast … max = Ultra), to name per-effort values. */
  depthNames: Record<string, string>;
}

export interface AdoptionStats {
  activeToday: number;
  activeWeek: number;
  activeMonth: number;
  totalUsers: number;
  stickiness: number;
  tokensToday: number;
  tokensWeek: number;
  tokensMonth: number;
  costToday: number;
  costWeek: number;
  costMonth: number;
  topUsers: UserSpendRow[];
  requestsTotal: number;
  costTotal: number;
  apps: number;
  since: string;
  updatedAt: string;
}

/** One row of the admin-owned model catalog (mirrors gyro-svc settings.ModelCatalogEntry). */
export interface ModelCatalogEntry {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
  /** Roles allowed to pick this model; empty/undefined = everyone. */
  allowedRoles?: string[];
  /** At most one entry may be the default (what an ask with no override runs on). */
  default?: boolean;
  /** The chat tier this entry answers: 'fast' or 'high' (High = per chat only, open to everyone, never the default). */
  tier?: 'fast' | 'high';
  /** The API the id runs on; absent means Anthropic. An OpenAI entry is never the default and carries no chat tier. */
  provider?: ModelProviderKind;
}

/** The two APIs a catalog entry can run on (gyro-core modelProviders.ts). */
export type ModelProviderKind = 'anthropic' | 'openai';

export interface ConfigStatus {
  model: string;
  cheapModel?: string;
  fallbackModel?: string;
  models: ModelCatalogEntry[];
  autonomy: 'off' | 'propose' | 'auto';
  pauseAutonomy: boolean;
  hasAnthropicKey: boolean;
  anthropicKeySource: 'admin' | 'env' | 'none';
  /** The OpenAI key (owner, 2026-10-22); absent on a service from before OpenAI models. */
  hasOpenAiKey?: boolean;
  openAiKeySource?: 'admin' | 'env' | 'none';
  hasElevenKey: boolean;
  voiceId: string;
  voiceSettings: VoiceSettings;
  /** The ElevenLabs model every app speaks with (their spelling, e.g. eleven_v4). */
  voiceModel: string;
  /** The effect on the spoken voice: none, or the light robot treatment the owner approved on 2026-10-13. */
  voiceEffect: VoiceEffect;
  budget: BudgetSettings;
  persona: { name?: string; greeting?: string; houseRules?: string };
  advisoryDisclaimer?: string;
  encryptionAvailable: boolean;
  updatedBy?: string;
  updatedAt?: string;
}

export interface SurfaceInfo {
  key: string;
  toolCount: number;
  tools: Array<{ name: string; mode: string; riskTier?: string; autonomousEligible: boolean; reversible?: boolean; requiredRoles: string[] }>;
  /** Remote (app-hosted) surfaces only: discovery state, and what signs the delegated identities sent to the receiver. */
  discovery?: { status: 'ok' | 'partial' | 'empty' | 'failed'; assertionSigning?: 'dedicated' | 'channel-token' };
  /** What verifies this app's callers today, by env NAME (audit 2026-10-03 High 15). Absent for Hub surfaces (Entra). */
  inboundKey?: { present: boolean; source: 'own' | 'shared' | 'none'; envs: string[]; noAppKeyByDesign?: boolean };
}

/** GET /v1/admin/surfaces `assertionKeys`: the per-app key rollout at a glance (docs/OPS-assertion-keys-2026-10-03.md). */
export interface AssertionKeysStatus {
  /** GYRO_ASSERTION_KEYS_STRICT=1: no shared-key fallback, channel-token receivers out of cross-app projection. */
  strict: boolean;
  sharedKeyApps: string[];
  refusedApps: string[];
  sharedValues: string[];
  channelTokenReceivers: string[];
  projectionExcluded: string[];
}

/** GET /v1/admin/lab — "Gyro, report this bug" status (counts only, never bug text). */
export interface LabStatus {
  registered: boolean;
  enabled: boolean;
  toolNames: string[];
  discovery: { status: 'ok' | 'partial' | 'empty' | 'failed'; toolCount: number; rejectedCount: number; lastError: string | null; lastSuccessAt: number | null } | null;
  uploadOriginConfigured: boolean;
  filings: { since: number; total: number; unassigned: number; byApp: Record<string, number>; lastAt: Record<string, number> };
}

export interface AccessTool {
  name: string;
  mode: string;
  riskTier: string;
  baseRoles: string[];
  override: string[] | null; // null = not overridden (using baseRoles)
  effectiveRoles: string[];
  andTighten: string[]; // extra roles ALSO required via the Apps-screen toolRoles override (AND, not replace)
}
export interface AccessSurface {
  key: string;
  tools: AccessTool[];
}
export interface PendingAccessChange {
  id: string;
  app: string;
  tool: string;
  oldRoles: string[];
  newRoles: string[];
  requestedBy: string;
  at: number;
  note?: string;
}

export type RiskTier = 'standard' | 'sensitive' | 'financial' | 'security' | 'break-glass';

export interface AppPolicy {
  key: string;
  toolCount: number;
  enabled: boolean;
  autonomy: 'off' | 'propose' | 'auto' | null;
  maxAutonomousRiskTier?: RiskTier;
  requireReversibleForAuto?: boolean;
  approvalRequiredAtOrAbove?: RiskTier | null;
  toolRoles?: Record<string, string[]>;
  /** Anthropic server-side web search for this app (defaults: enabled, 10 searches per request). */
  webSearch?: WebSearchPolicyStatus;
}

export interface WebSearchPolicyStatus {
  enabled: boolean;
  /** Searches per model request the app runs at: its own value, else the Usage caps default. */
  maxUses: number;
  /** The app's own stored value, or null when it follows the Usage caps default. */
  ownMaxUses: number | null;
  allowedDomains: string[];
  blockedDomains: string[];
  userLocation?: Record<string, string>;
}

/** PUT /v1/admin/apps/:key {webSearch}: the stored policy, replaced whole. maxUses null means 'use the Usage caps default'. */
export interface WebSearchPolicyPatch {
  enabled?: boolean;
  maxUses?: number | null;
  allowedDomains?: string[];
  blockedDomains?: string[];
  userLocation?: Record<string, string>;
}

export interface TraceRow {
  traceId: string;
  ts: string;
  app: string;
  userHash: string;
  trust: string;
  model: string;
  /** The API the model ran on; absent on rows written before OpenAI models existed (Anthropic). */
  provider?: ModelProviderKind;
  rounds: number;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  /** Prompt-cache tokens (trace.ts); absent when 0 and on rows written before 2026-10-12. */
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  costUsd: number;
  stopReason?: string;
  error?: string;
  tools: Array<{ name: string; mode: string; decision: string; riskTier?: string; ms: number; ok: boolean }>;
  skills: Array<{ id: string; score: number }>;
  pendingCount: number;
  autoCount: number;
  /** Length of the question; the text is never stored (privacy policy: the usage log holds a hashed id, not the conversation). */
  questionChars?: number;
  rating?: 'up' | 'down';
}

/** One company skill as GET /v1/admin/company-skills lists it (userSkillAdminRoutes.ts companySkillView). */
export interface CompanySkillRow {
  id: string;
  name: string;
  description: string;
  aliases: string[];
  body: string;
  version: number;
  status: 'active' | 'retired';
  /** The person who wrote it (a promoted skill carries its author's name). */
  authorName?: string;
  publishedBy: string;
  createdAt: string;
  updatedAt: string;
  source: 'chat' | 'package' | 'admin';
  retiredAt?: string;
  retiredBy?: string;
  /** Where a promoted skill came from; `userId` is gone once that person has been erased. */
  promotedFrom?: { userId?: string; skillId: string };
  /** Every published revision, newest first (the current one included). */
  history: Array<{ version: number; name: string; description: string; aliases: string[]; at: string; by: string }>;
}

export interface CompanySkillsView {
  skills: CompanySkillRow[];
  /** The limits the service holds a skill to, by name (userSkills.ts SKILL_LIMITS). */
  limits: CompanySkillLimits;
}

/** The limits a company skill is held to (gyro-svc userSkills.ts SKILL_LIMITS); the drawer checks them before sending. */
export interface CompanySkillLimits {
  companySkills: number;
  name: number;
  description: number;
  aliases: number;
  alias: number;
  /** The fewest characters of one alias (absent from a service before 2026-10-12: 3). */
  aliasMin?: number;
  body: number;
}

/** PUT /v1/admin/company-skills/:id ('new' publishes). */
export interface CompanySkillInput {
  name: string;
  description: string;
  aliases: string[];
  body: string;
  authorName?: string | null;
}

/** One of a person's own skills (GET /v1/admin/people/:id/skills); the body comes only to an admin. */
export interface PersonSkillRow {
  id: string;
  name: string;
  description: string;
  aliases: string[];
  body?: string;
  createdAt: string;
  updatedAt: string;
  source: 'chat' | 'package' | 'admin';
}

export interface PersonSkillsView {
  /** The Entra object id the skills are stored under. */
  userId: string;
  skills: PersonSkillRow[];
}

export interface AuditRow {
  seq: number;
  ts: string;
  hash: string;
  prevHash: string;
  actor: { id: string; email?: string; trust?: string };
  app?: string;
  event: string;
  tool?: string;
  riskTier?: string;
  outcome?: string;
  detail?: string;
}

export type DesktopReconciliationDisposition =
  | 'operator-confirmed-applied'
  | 'operator-confirmed-not-applied'
  | 'operator-confirmed-partial-or-wrong-state';

export interface AdminOperatorDisposition {
  outcome: 'applied' | 'not-applied' | 'partial-or-wrong-state';
  source?: 'client-reported-uncertain' | 'lease-expired';
  evidenceReference: string;
  evidenceSha256: string;
  evidenceObservedAt?: number;
  claimId?: string;
  deviceId?: string;
  decidedById?: string;
  decidedByEmail: string;
  decidedAt: number;
}

export interface AdminApproval {
  id: string;
  app: string;
  tool: string;
  summary: string;
  riskTier?: string;
  input: Record<string, unknown>;
  at: number;
  status: string;
  stateVersion: number;
  recordProof: string;
  requiresApproval: boolean;
  requestedBy: string;
  governedPreview?: { previewId: string; inputHash: string; preview: Record<string, unknown>; expiresAt: number; implementationRevision: string };
  run?: Record<string, unknown>;
  errorMessage?: string;
  operatorDisposition?: AdminOperatorDisposition;
  lateClientReceipt?: { outcome: string; message?: string; settledAt: number; conflictsWithDisposition: boolean };
}

export interface DesktopReconciliationRequest {
  disposition: DesktopReconciliationDisposition;
  evidenceReference: string;
  evidenceSha256: string;
  evidenceObservedAt: number;
  acknowledgeNoReplay: true;
  expectedStateVersion: number;
  expectedRecordProof: string;
}

export interface ReconciliationResponse {
  status: string;
  message?: string;
  dispositionRequired?: boolean;
  settleAfter?: number;
  resultReady?: boolean;
  source?: 'client-reported-uncertain' | 'lease-expired';
  expectedStateVersion?: number;
  expectedRecordProof?: string;
  operatorDisposition?: AdminOperatorDisposition;
  reused?: boolean;
}

/**
 * One approval-card kind in the window (services/gyro-svc/src/approvalAnalytics.ts): counts and seconds only, never a
 * person. Each card proposed in the window is in exactly one of the five states (proposed = their sum), read from its
 * ledger rows as a state machine: proposed -> confirmed | cancelled | awaiting (submitted for approval), and awaiting
 * -> confirmed (approved) | rejected | cancelled. A card that nobody decides writes no row when it expires, so
 * `undecided` is a proposal with no deciding row: still open, expired, or ended by a tool setting.
 */
export interface ApprovalCardRow {
  app: string;
  tool: string;
  proposed: number;
  /** The person confirmed (and, where an approver was needed, the approver approved). */
  confirmed: number;
  /** The person cancelled. */
  cancelled: number;
  /** The person confirmed and an approver rejected. */
  rejected: number;
  /** The person confirmed and an approver has not decided yet. */
  awaiting: number;
  /** No decision by anyone: still open, expired unanswered, or ended by a tool setting. */
  undecided: number;
  /** Median seconds from the proposal to the person's own decision (confirm, submit for approval or cancel), or null with none timed. */
  medianSecondsToDecision: number | null;
  /** How many cards the median rests on. */
  timed: number;
}

export interface Analytics {
  since: string | null;
  totals: { requests: number; inputTokens: number; outputTokens: number; costUsd: number; errors: number; avgMs: number };
  perApp: Array<{ app: string; requests: number; costUsd: number; errors: number; avgMs: number }>;
  perTool: Array<{ tool: string; calls: number; denied: number; proposed: number; autoExecuted: number; blocked: number; errors: number }>;
  perSkill: Array<{ id: string; retrievals: number; avgScore: number }>;
  quality: { up: number; down: number; rated: number };
  topUsers: Array<{ userHash: string; requests: number; costUsd: number }>;
  /** Absent from a service older than the approval-card analytics. */
  approvals?: ApprovalCardRow[];
}

export interface SkillRow {
  id: string;
  name: string;
  description: string;
  restricted: boolean;
  enabled: boolean;
  requiredRoles: string[];
  meta: { lastVerified?: string; riskLevel?: string; writesExternalSystems?: boolean; updateCadence?: string };
}

export interface Webhook {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  hasSecret: boolean;
  /** Set on an endpoint registered before the Bridge rule on a Teams/Slack/Workflows host (still delivers). */
  forbiddenChannel?: 'teams' | 'slack' | 'workflows';
  createdBy?: string;
  createdAt?: string;
}

export interface FeatureFlag {
  enabled: boolean;
  percent?: number;
  roles?: string[];
}

export interface DashModelRow {
  model: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}
export interface DashAppRow {
  app: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  errors: number;
  lastUsedAt?: string;
  byModel: DashModelRow[];
}
export interface Dashboard {
  total: { requests: number; inputTokens: number; outputTokens: number; costUsd: number; apps: number };
  perApp: DashAppRow[];
  byModel: DashModelRow[];
  since: string;
  updatedAt: string;
  voice: { tier: string | null; characterCount: number; characterLimit: number; remaining: number } | null;
  /**
   * What the voice row says (owner 2026-10-31): `none` no ElevenLabs key; `usage` the key's usage is in `voice`;
   * `usage_unreadable` a key is stored but ElevenLabs would not give its usage (a key without its User read permission, an
   * expired key, no answer), so `voice` is null although a key is set. Absent from an older service, where `voice` null
   * reads as no key, as it did.
   */
  voiceStatus?: 'none' | 'usage' | 'usage_unreadable';
  surfaces: string[];
  appsEnabled: number;
  skillCount?: number;
  budget?: BudgetSettings;
  spend?: { day: number; month: number };
  /**
   * Whether the spend figures rest on a successful read of the stored ledgers. `hydrated: false` means the service
   * could not read its ledger yet and the fleet caps count only the current process's turns (it keeps retrying).
   */
  ledgers?: { spend: LedgerState; usageQuota: LedgerState };
  /** Saved-chat storage: the legacy document's size against its ceiling and saves refused there (sizes and counts only). */
  historyStorage?: HistoryStorageState;
  /** What Gyro could not do in the last 30 days (counts by category and kind, never a person or a request; lib/unmet.ts). */
  unmet?: UnmetSummary;
}

export interface UnmetSummary {
  windowDays: number;
  total: number;
  rows: Array<{ category: string; kind: string; count: number }>;
  bySurface: Array<{ surface: string; count: number }>;
  hydrated: boolean;
}

export interface LedgerState {
  name: string;
  hydrated: boolean;
  dirty: boolean;
  hydrateAttempts: number;
  flushFailures: number;
}

export interface SharedVoice {
  voiceId: string;
  publicOwnerId: string;
  name: string;
  accent?: string;
  gender?: string;
  age?: string;
  useCase?: string;
  previewUrl?: string;
}

// IT Support — remote diagnostic sessions (consent-gated, read-only collectors)
export type SupportSessionState = 'requested' | 'awaiting-consent' | 'active' | 'suspended' | 'ended' | 'denied';
export type SupportSessionMode = 'attended-consent' | 'notice-then-proceed' | 'auto-accept-silent';
export type SupportCommandStatus = 'pending' | 'acked' | 'completed' | 'failed' | 'timedout';
export type SupportTelemetryStatus = 'ok' | 'denied' | 'timeout' | 'stale';

export interface SupportDeviceHealth {
  version: string;
  channel?: string;
  healthy: boolean;
  crashCount?: number;
  lastError?: string;
  reportedAtMs?: number;
  at: number;
}

export interface SupportDevice {
  deviceId: string;
  publicKeySha256: string;
  purpose: 'user' | 'broker-audit';
  status: 'active' | 'revoked';
  machineName?: string;
  /** Who vouched for the key (the authorizing admin, or the person for a self-enrolled key). */
  enrolledBy?: string;
  enrolledAtMs?: number;
  lastSeenAtMs?: number;
  /** Whose PC it is: the employee who last enrolled the key with their validated Entra bearer (absent until one did). */
  ownerObjectId?: string;
  ownerEmail?: string;
  revokedAtMs?: number;
  revokedBy?: string;
  revocationReason?: string;
  health: SupportDeviceHealth | null;
}

export interface DeviceEnrollmentAuthorization {
  authorizationId: string;
  deviceId: string;
  publicKeySha256: string;
  purpose: 'user' | 'broker-audit';
  replacesPublicKeySha256?: string;
  authorizedByEmail: string;
  authorizedAtMs: number;
  expiresAtMs: number;
  machineName?: string;
}

export interface DeviceKeyMigration {
  migrationId: string;
  deviceId: string;
  oldPublicKeySha256: string;
  newPublicKeySha256: string;
  purpose: 'user' | 'broker-audit';
  authorizationId: string;
  machineName?: string;
  subjectId: string;
  subjectTenantId: string;
  subjectObjectId: string;
  subjectEmail: string;
  status: 'accepted' | 'verified' | 'retired';
  acceptedAtMs: number;
  verifiedAtMs?: number;
  retiredAtMs?: number;
}

export interface OtaProposal {
  proposalId: string;
  releaseSha256: string;
  createdByEmail: string;
  createdAtMs: number;
  expiresAtMs: number;
  status: 'pending' | 'approved' | 'publishing' | 'published';
  allowRollback: boolean;
  rollbackReason?: string;
  approvedByEmail?: string;
  approvedAtMs?: number;
  publishedAtMs?: number;
  review: {
    type: 'update-available' | 'update-now' | 'avatar-pack';
    deviceIds: string[] | 'all';
    manifest: {
      version: string;
      channel: 'canary' | 'early' | 'broad';
      kind: 'app' | 'avatar';
      packageLocation: string;
      sha256: string;
      sizeBytes: number;
      mandatory: boolean;
      minFromVersion?: string;
      rollback: boolean;
      releaseNotes?: string;
    };
  };
}

/** Push-frame delivery result. ok=false means the device did NOT receive the frame (offline / other replica). */
export interface SupportDelivery {
  ok: boolean;
  applied?: boolean;
  outcome: 'pending' | 'delivered' | 'no-channel' | 'write-failed' | 'partial' | 'already-accepted' | 'unsigned-dropped' | 'outbox-failed';
}

export interface SupportGateResult {
  action: 'apply-fix' | 'collect-more' | 'escalate';
  allowFix: boolean;
  topConfidence: number;
  coverageRatio: number;
  coverage: { planned: number; ran: number; denied: number; timedOut: number; stale: number };
  reasons: string[];
}

export interface SupportSessionSummary {
  sessionId: string;
  state: SupportSessionState;
  targetDeviceId: string;
  targetUserEmail?: string;
  operatorEmail: string;
  reason: string;
  requestedMode: SupportSessionMode;
  legalBasis: string;
  consent?: { decision: 'granted' | 'denied'; byEmail?: string; atMs: number };
  telemetryCount: number;
  lastSeq: number;
  pendingCommands: number;
  createdAtMs: number;
  updatedAtMs: number;
  endReason?: string;
  lastGate?: SupportGateResult;
}

export interface SupportCommand {
  commandId: string;
  seq: number;
  tool: string;
  args?: unknown;
  status: SupportCommandStatus;
  issuedAtMs: number;
  deadlineMs: number;
  result?: unknown;
  error?: string;
}

export interface SupportTelemetryItem {
  id: string;
  collector: string;
  status: SupportTelemetryStatus;
  ageMs?: number;
  commandId?: string;
}

export interface SupportSessionDetail {
  session: SupportSessionSummary;
  commands: SupportCommand[];
  telemetry: SupportTelemetryItem[];
}

// User-initiated support requests (§3.6): the target user raises a hand; operators claim → session → resolve.
export type SupportRequestStatus = 'open' | 'claimed' | 'resolved';

export interface SupportRequest {
  requestId: string;
  deviceId: string;
  machineName?: string;
  userEmail?: string;
  summary: string;
  status: SupportRequestStatus;
  createdAtMs: number;
  updatedAtMs: number;
  claimedBy?: string;
  resolvedBy?: string;
}

// Owner decision 3 (2026-10-06): Opus 5.5 is the default, Sonnet 5.5 the fast and cheap model, Opus 4.8 the fallback.
// Sonnet 5 stays selectable so a saved setting still shows its name and the change can be reverted from this screen.
export const MODELS = [
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5 (recommended)' },
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 (fast)' },
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
  { id: 'claude-fable-5', label: 'Claude Fable 5' },
];

export const api = {
  petOffboardingReview: (subjectId: string, signal?: AbortSignal) => http<PetOffboardingReview>(`/v1/admin/pets/owners/${encodeURIComponent(subjectId)}/offboarding`, { signal }),
  offboardPetOwner: (subjectId: string, body: { reviewHash: string; reason: string }, key: string) => http<PetOffboardingReceipt>(`/v1/admin/pets/owners/${encodeURIComponent(subjectId)}/offboard`, { method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify(body) }),
  petSupport: (signal?: AbortSignal) => http<PetSupportStatus>('/v1/admin/pets/support', { signal }),
  petLibrary: (cursor?: string, signal?: AbortSignal) => http<PetReviewQueue>(`/v1/admin/pets/library?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, { signal }),
  moderatePet: (id: string, body: PetModerationInput, key: string) => http<{ package: PetModerationPackage }>(`/v1/admin/pets/library/${encodeURIComponent(id)}/moderate`, {
    method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify(body),
  }),
  me: async (): Promise<{ user: AdminUser | null; authConfigured?: boolean }> => {
    const res = await fetch('/auth/me', { credentials: 'include' });
    if (res.status === 401) {
      const body = (await res.json().catch(() => ({}))) as { authConfigured?: boolean };
      return { user: null, authConfigured: body.authConfigured };
    }
    if (!res.ok) throw new ApiError('me failed', res.status);
    return (await res.json()) as { user: AdminUser };
  },
  logout: () => http<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  getConfig: () => http<ConfigStatus>('/v1/admin/config'),
  saveConfig: (
    // expectedGlobalCaps: the value the screen loaded for each global cap `budget` changes (0 = not set); the service
    // refuses the save with 409 global_caps_changed when one is no longer what is stored (#232).
    patch: Partial<{ model: string; cheapModel: string | null; fallbackModel: string | null; models: ModelCatalogEntry[] | null; autonomy: string; pauseAutonomy: boolean; budget: BudgetSettings; expectedGlobalCaps: BudgetSettings; limits: Record<string, unknown>; advisoryDisclaimer: string | null; anthropicKey: string | null; openaiKey: string | null; elevenLabsKey: string | null; voiceId: string; voiceSettings: Partial<VoiceSettings>; voiceModel: string | null; voiceEffect: VoiceEffect }>,
  ) => http<ConfigStatus>('/v1/admin/config', { method: 'PUT', body: JSON.stringify(patch) }),
  testConfig: () => http<{ ok: boolean; model?: string; message?: string }>('/v1/admin/config/test', { method: 'POST', body: '{}' }),
  /** "Check id" on a catalog row: GET /v1/models/{id} on the provider's API with the service's key (status and a sentence only). */
  checkModel: (id: string, provider: ModelProviderKind) => http<{ ok: boolean; status: number; message: string; provider: ModelProviderKind }>('/v1/admin/config/models/check', { method: 'POST', body: JSON.stringify({ id, provider }) }),
  getUsageCaps: () => http<UsageCapsStatus>('/v1/admin/usage-caps'),
  adoption: (top = 10) => http<AdoptionStats>(`/v1/admin/adoption?top=${top}`),
  setUsageCaps: (change: UserCapsChange) =>
    http<UsageCapsStatus>('/v1/admin/usage-caps', { method: 'PUT', body: JSON.stringify(change) }),
  surfaces: () => http<{ surfaces: SurfaceInfo[]; assertionKeys?: AssertionKeysStatus }>('/v1/admin/surfaces'),
  lab: () => http<LabStatus>('/v1/admin/lab'),

  // Access & Roles
  access: () => http<{ surfaces: AccessSurface[]; pending: PendingAccessChange[] }>('/v1/admin/access'),
  setAccess: (app: string, tool: string, roles: string[], note?: string) =>
    http<{ status: 'applied' | 'pending_approval'; changeId?: string }>(
      `/v1/admin/access/${encodeURIComponent(app)}/${encodeURIComponent(tool)}`,
      { method: 'PUT', body: JSON.stringify({ roles, note }) },
    ),
  decideAccess: (id: string, decision: 'approve' | 'reject') =>
    http<{ status: string; message?: string }>(`/v1/admin/access/approvals/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),
  // Company knowledge library (docs/CONTRACT-company-knowledge-import.md sections 3 to 6; the Knowledge screen)
  knowledgeStatus: () => http<KnowledgeStatus>('/v1/admin/knowledge/status'),
  knowledgeProjects: () => http<ProjectsView>('/v1/admin/knowledge/projects'),
  knowledgePutProjects: (body: { projects: ProjectInput[]; folders: Array<{ path: string; scope: 'internal' | 'none'; notes?: string }>; exclusions: string[]; expectedRevision: number }) =>
    http<ProjectsView>('/v1/admin/knowledge/projects', { method: 'PUT', body: JSON.stringify(body) }),
  knowledgePatchProject: (id: string, patch: Partial<Omit<ProjectInput, 'id'>> & { expectedRevision: number }) =>
    http<{ revision: number; project: ProjectView }>(`/v1/admin/knowledge/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  knowledgePools: () => http<PoolsView>('/v1/admin/knowledge/pools'),
  knowledgePutPools: (body: { pools: PoolRow[]; expectedRevision: number }) => http<PoolsView>('/v1/admin/knowledge/pools', { method: 'PUT', body: JSON.stringify(body) }),
  knowledgeSources: (scope: ScopeText, page: number, pageSize: number) =>
    http<SourcesPage>(`/v1/admin/knowledge/sources?scope=${encodeURIComponent(scope)}&page=${page}&pageSize=${pageSize}`),
  knowledgeDeleteSource: (scope: ScopeText, path: string) =>
    http<{ status: 'deleted' | 'absent' }>(`/v1/admin/knowledge/sources?scope=${encodeURIComponent(scope)}&path=${encodeURIComponent(path)}`, { method: 'DELETE' }),
  knowledgeRequestRemoval: (id: string, reason: string) =>
    http<{ status: string; removalId: string; projectId: string }>(`/v1/admin/knowledge/projects/${encodeURIComponent(id)}/remove`, { method: 'POST', body: JSON.stringify({ reason }) }),
  knowledgeRemovals: () => http<{ removals: RemovalRow[] }>('/v1/admin/knowledge/removals'),
  knowledgeDecideRemoval: (id: string, decision: 'approve' | 'reject') =>
    http<{ status: string; projectId: string; counts?: Record<string, number> }>(`/v1/admin/knowledge/removals/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ decision }) }),
  knowledgeReport: (party: string) => http<ReportView>(`/v1/admin/knowledge/report?party=${encodeURIComponent(party)}`),

  dashboard: () => http<Dashboard>('/v1/admin/dashboard'),
  skills: () => http<{ skills: SkillRow[] }>('/v1/admin/skills'),
  setSkill: (id: string, patch: { enabled?: boolean; requiredRoles?: string[] }) =>
    http<{ skills: SkillRow[] }>(`/v1/admin/skills/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) }),
  reloadSkills: () => http<{ count: number }>('/v1/admin/skills/reload', { method: 'POST', body: '{}' }),
  // Company skills (gyro-svc userSkillAdminRoutes.ts; dark until GYRO_COMPANY_SKILLS_ENABLED=1, when the GET is 404).
  companySkills: () => http<CompanySkillsView>('/v1/admin/company-skills'),
  publishCompanySkill: (id: string, body: CompanySkillInput) =>
    http<CompanySkillRow>(`/v1/admin/company-skills/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  retireCompanySkill: (id: string) => http<CompanySkillRow>(`/v1/admin/company-skills/${encodeURIComponent(id)}/retire`, { method: 'POST', body: '{}' }),
  promoteSkill: (body: { userId: string; skillId: string; authorName: string }) =>
    http<CompanySkillRow>('/v1/admin/company-skills/promote', { method: 'POST', body: JSON.stringify(body) }),
  personSkills: (id: string) => http<PersonSkillsView>(`/v1/admin/people/${encodeURIComponent(id)}/skills`),

  // Activity / analytics / audit
  analytics: (days = 30) => http<Analytics>(`/v1/admin/analytics?days=${days}`),
  traces: (filter: { app?: string; errors?: boolean; thumbsDown?: boolean; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (filter.app) p.set('app', filter.app);
    if (filter.errors) p.set('errors', '1');
    if (filter.thumbsDown) p.set('thumbsDown', '1');
    if (filter.limit) p.set('limit', String(filter.limit));
    return http<{ traces: TraceRow[] }>(`/v1/admin/traces?${p}`);
  },
  trace: (id: string) => http<{ trace: TraceRow }>(`/v1/admin/traces?id=${encodeURIComponent(id)}`),
  audit: (filter: { app?: string; event?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (filter.app) p.set('app', filter.app);
    if (filter.event) p.set('event', filter.event);
    if (filter.limit) p.set('limit', String(filter.limit));
    return http<{ audit: AuditRow[]; chain: { ok: boolean; brokenAtSeq?: number }; capacity?: AuditCapacity; lifecycle?: LifecycleFaultSummary }>(`/v1/admin/audit?${p}`);
  },
  approvals: (app?: string) =>
    http<{ approvals: AdminApproval[]; legacyQuarantine: Array<{ recordSha256: string; quarantinedAt: number; reason: 'legacy-unsealed-record'; actionId: string; app: string; userId: string; priorStatus: string; summary: string }> }>(
      `/v1/admin/approvals${app ? `?app=${encodeURIComponent(app)}` : ''}`,
    ),
  decideApproval: (id: string, decision: 'approve' | 'reject', governedPreview?: { previewId: string; implementationRevision: string }) => http<{ status: string; message?: string }>(`/v1/admin/approvals/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify({
      decision,
      ...(decision === 'approve' && governedPreview ? {
        acknowledgePreview: true,
        previewId: governedPreview.previewId,
        implementationRevision: governedPreview.implementationRevision,
      } : {}),
    }),
  }),
  reconcileApproval: (
    id: string,
    disposition?: DesktopReconciliationRequest | {
      disposition: 'operator-confirmed-not-applied';
      evidenceReference: string;
      evidenceSha256: string;
      acknowledgeNoReplay: true;
    },
  ) => http<ReconciliationResponse>(`/v1/admin/approvals/${encodeURIComponent(id)}/reconcile`, {
    method: 'POST',
    body: JSON.stringify(disposition ?? {}),
  }),

  // Persona
  persona: () => http<{ persona: { name?: string; greeting?: string; houseRules?: string }; history: Array<{ name?: string; greeting?: string; houseRules?: string; at: string; by: string }> }>('/v1/admin/persona'),
  savePersona: (patch: { name?: string; greeting?: string; houseRules?: string }) => http<{ persona: unknown }>('/v1/admin/persona', { method: 'PUT', body: JSON.stringify(patch) }),

  // Feature flags
  flags: () => http<{ flags: Record<string, FeatureFlag> }>('/v1/admin/flags'),
  setFlag: (key: string, patch: FeatureFlag) => http<{ flags: Record<string, FeatureFlag> }>(`/v1/admin/flags/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(patch) }),

  // Redaction / DLP
  redaction: () => http<{ policy: { denyTerms?: string[]; disabled?: string[]; actions?: Record<string, string> } }>('/v1/admin/redaction'),
  setRedaction: (policy: { denyTerms?: string[]; disabled?: string[]; actions?: Record<string, string> }) => http<{ policy: unknown }>('/v1/admin/redaction', { method: 'PUT', body: JSON.stringify(policy) }),

  // Sensitivity labels and Office installs (governance, lib/governance.ts). Office installs answer 404 until the
  // service runs the Office add-in (GYRO_OFFICE_ENABLED); the console then hides that screen.
  labelPolicy: () => http<LabelPolicyStatus>('/v1/admin/label-policy'),
  setLabelPolicy: (policy: LabelPolicy) => http<LabelPolicyStatus>('/v1/admin/label-policy', { method: 'PUT', body: JSON.stringify({ policy }) }),
  // The read-only tools of the PCs' native MCP servers (Apps, lib/mcpReadOnly.ts): calls to them run without a card. null = the seed.
  mcpReadOnlyTools: () => http<McpReadOnlyView>('/v1/admin/mcp-read-only-tools'),
  setMcpReadOnlyTools: (servers: McpReadOnlyServer[] | null) => http<McpReadOnlyView>('/v1/admin/mcp-read-only-tools', { method: 'PUT', body: JSON.stringify({ servers }) }),
  // "Create my PPP" (Governance card, lib/ppp.ts). All three routes answer the full status shape (one mirror type).
  ppp: () => http<PppAdminView>('/v1/admin/ppp'),
  pppStatus: () => http<PppAdminView>('/v1/admin/ppp/status'),
  savePpp: (patch: PppAdminPatch) => http<PppAdminView>('/v1/admin/ppp', { method: 'PUT', body: JSON.stringify(patch) }),
  // StarScribe (Governance card, lib/starscribe.ts): the settings, counts only, and a leaver's pages handed to a colleague.
  starscribe: () => http<StarscribeAdminView>('/v1/admin/starscribe'),
  saveStarscribe: (patch: StarscribeAdminPatch) => http<StarscribeAdminView>('/v1/admin/starscribe', { method: 'PUT', body: JSON.stringify(patch) }),
  starscribeStatus: () => http<StarscribeAdminStatus>('/v1/admin/starscribe/status'),
  starscribeHandoff: (from: string, to: string) => http<{ moved: number }>('/v1/admin/starscribe/handoff', { method: 'POST', body: JSON.stringify({ from, to }) }),
  // The people picker on the settings that list people (components/PeoplePicker.tsx, lib/people.ts). IT admins only.
  people: (query: string, limit: number) => http<PeopleSearch>(`/v1/admin/people?q=${encodeURIComponent(query)}&limit=${limit}`),
  // Operations › Analytics (the usage analytics contract, section 2): every admin read under /v1/admin/usage takes the lens
  // (window, surface, audience, hide_admin); reads for admins and auditors, the audience writes for admins. The answers are
  // read by lib/usage.ts's normalizers, so the wire types here are the loose `unknown` and nothing is trusted before them.
  usageFeatures: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/features${usageQueryString(query)}`),
  usageFeature: (kind: string, name: string, query: UsageQuery) => http<unknown>(`/v1/admin/usage/features/${encodeURIComponent(kind)}/${encodeURIComponent(name)}${usageQueryString(query)}`),
  usageRealtime: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/realtime${usageQueryString(query)}`),
  usageSessions: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/sessions${usageQueryString(query)}`),
  usageJourneys: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/journeys${usageQueryString(query)}`),
  usageRetention: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/retention${usageQueryString(query)}`),
  usagePeople: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/people${usageQueryString(query)}`),
  usageTech: (query: UsageQuery) => http<unknown>(`/v1/admin/usage/tech${usageQueryString(query)}`),
  usageCatalog: () => http<unknown>('/v1/admin/usage/catalog'),
  usageAudience: () => http<unknown>('/v1/admin/usage/audience'),
  setUsageAudience: (upns: string[], internal: boolean) => http<unknown>('/v1/admin/usage/audience', { method: 'PUT', body: JSON.stringify({ upns, internal }) }),
  importUsageAudience: (groupId: string) => http<unknown>('/v1/admin/usage/audience/import', { method: 'POST', body: JSON.stringify({ group_id: groupId }) }),
  // Pulse (docs/CONTRACT-pulse.md section 6.3): every route under /v1/admin/pulse. Reads for admins and auditors, writes for
  // admins; 404 `pulse_disabled` on all of them while the flag is off (App.tsx probes pulseSettings and hides the rail group).
  pulseSettings: () => http<PulseSettingsView>('/v1/admin/pulse/settings'),
  savePulseSettings: (patch: PulseSettingsPatch) => http<PulseSettingsView>('/v1/admin/pulse/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  pulseOverview: (filter: PulseQueryParams = {}) => http<PulseOverview>(`/v1/admin/pulse/overview${pulseQuery(filter)}`),
  pulseAsks: (filter: PulseQueryParams = {}) => http<PulseList<PulseIntentRow>>(`/v1/admin/pulse/asks${pulseQuery(filter)}`),
  pulseIntent: (intentKey: string, filter: PulseQueryParams = {}) => http<PulseIntentDetail>(`/v1/admin/pulse/asks/${encodeURIComponent(intentKey)}${pulseQuery(filter)}`),
  pulseRecords: (filter: PulseQueryParams = {}) => http<PulseList<PulseRecord>>(`/v1/admin/pulse/records${pulseQuery(filter)}`),
  pulseRecord: (askId: string) => http<PulseRecord>(`/v1/admin/pulse/records/${encodeURIComponent(askId)}`),
  pulseDomain: (domain: string, filter: PulseQueryParams = {}) => http<PulseDomainScorecard>(`/v1/admin/pulse/domains/${encodeURIComponent(domain)}${pulseQuery(filter)}`),
  pulseSubjects: (filter: PulseQueryParams = {}) => http<PulseList<PulseSubjectRow>>(`/v1/admin/pulse/subjects${pulseQuery(filter)}`),
  pulseSubjectAction: (subjectId: string, action: PulseSubjectAction) =>
    http<{ subject: PulseSubjectRow }>(`/v1/admin/pulse/subjects/${encodeURIComponent(subjectId)}/${action}`, { method: 'POST', body: '{}' }),
  pulseFeedback: (filter: PulseQueryParams = {}) => http<PulseList<PulseFeedbackRow>>(`/v1/admin/pulse/feedback${pulseQuery(filter)}`),
  pulseFeedbackReviewed: (key: string, reviewed: boolean) =>
    http<{ entry: PulseFeedbackRow }>(`/v1/admin/pulse/feedback/${encodeURIComponent(key)}/reviewed`, { method: 'POST', body: JSON.stringify({ reviewed }) }),
  pulseFeedbackToProblem: (key: string, target: { problemId: string } | { new: { title: string } }) =>
    http<{ problem: PulseProblem; entry?: PulseFeedbackRow }>(`/v1/admin/pulse/feedback/${encodeURIComponent(key)}/problem`, { method: 'POST', body: JSON.stringify(target) }),
  pulseProblems: (filter: PulseQueryParams = {}) => http<PulseList<PulseProblem>>(`/v1/admin/pulse/problems${pulseQuery(filter)}`),
  pulseProblem: (problemId: string) => http<PulseProblem>(`/v1/admin/pulse/problems/${encodeURIComponent(problemId)}`),
  createPulseProblem: (body: { title: string; domain: string; intentKeys?: string[]; subjectIds?: string[]; evidence?: Array<{ kind: 'record' | 'feedback' | 'health'; id: string }> }) =>
    http<{ problem: PulseProblem }>('/v1/admin/pulse/problems', { method: 'POST', body: JSON.stringify(body) }),
  setPulseProblemStatus: (problemId: string, body: { status: PulseProblemStatus; version?: string; duplicateOf?: string; note?: string }) =>
    http<{ problem: PulseProblem }>(`/v1/admin/pulse/problems/${encodeURIComponent(problemId)}/status`, { method: 'POST', body: JSON.stringify(body) }),
  addPulseProblemNote: (problemId: string, text: string) =>
    http<{ problem: PulseProblem }>(`/v1/admin/pulse/problems/${encodeURIComponent(problemId)}/notes`, { method: 'POST', body: JSON.stringify({ text }) }),
  pulseDigests: (filter: PulseQueryParams = {}) => http<PulseList<PulseDigestListItem>>(`/v1/admin/pulse/digests${pulseQuery(filter)}`),
  pulseDigest: (digestId: string) => http<PulseDigest>(`/v1/admin/pulse/digests/${encodeURIComponent(digestId)}`),
  runPulseDigest: (kind: 'daily' | 'weekly') => http<{ digest: PulseDigest }>('/v1/admin/pulse/digests/run', { method: 'POST', body: JSON.stringify({ kind }) }),
  markPulseDigestRead: (digestId: string) => http<{ digest: PulseDigest }>(`/v1/admin/pulse/digests/${encodeURIComponent(digestId)}/read`, { method: 'POST', body: '{}' }),
  pulseOperations: (filter: PulseQueryParams = {}) => http<PulseOperations>(`/v1/admin/pulse/operations${pulseQuery(filter)}`),
  erasePulseText: () => http<{ erased: number }>('/v1/admin/pulse/erase-text', { method: 'POST', body: '{}' }),

  officeInstalls: (user?: string) => http<{ installs: OfficeInstall[] }>(`/v1/admin/office/installs${user ? `?user=${encodeURIComponent(user)}` : ''}`),
  revokeOfficeInstall: (installId: string) =>
    http<{ status: 'revoked' | 'already_revoked'; install: OfficeInstall }>(`/v1/admin/office/installs/${encodeURIComponent(installId)}`, { method: 'DELETE' }),

  // Desktop mascot gallery (Governance card, lib/desktopMascot.ts and lib/dayOutfits.ts). The routes exist only where the
  // service runs the desktop app (GYRO_DESKTOP_ENABLED): a 404 on the read means "not on this service", so it answers
  // null and the card hides. Every write sends the catalog revision last read (and the day outfits' annex revision where
  // the route takes it) and answers the whole catalog again.
  desktopMascot: async (): Promise<DesktopMascotCatalog | null> => {
    try {
      return await http<DesktopMascotCatalog>('/v1/admin/desktop/mascot');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  /** An outfit's full image as stored (the bytes desktops get), or null when it is gone or could not be read. */
  desktopMascotOutfitImage: async (id: string): Promise<Blob | null> => {
    const res = await fetch(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}/image`, { credentials: 'include' });
    return res.ok ? res.blob() : null;
  },
  addDesktopMascotOutfit: (body: AddMascotOutfitBody) =>
    http<DesktopMascotAdded>('/v1/admin/desktop/mascot/outfits', { method: 'POST', body: JSON.stringify(body) }),
  /** A day outfit: the same route, with a day rule, greeting and kind in place of a window. */
  addDesktopMascotDayOutfit: (body: AddDayOutfitBody) =>
    http<DesktopMascotAdded>('/v1/admin/desktop/mascot/outfits', { method: 'POST', body: JSON.stringify(body) }),
  /** A new name or window, and the Featured toggle (`featured`): that outfit joins or leaves the featured outfits; the others stay. */
  updateDesktopMascotOutfit: (id: string, body: UpdateMascotOutfitBody) =>
    http<DesktopMascotCatalog>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** A day outfit's rule, greeting, kind and collection (PUT …/day): it stays a day outfit. */
  updateDesktopMascotDay: (id: string, body: UpdateDayBody) =>
    http<DesktopMascotCatalog>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}/day`, { method: 'PUT', body: JSON.stringify(body) }),
  /**
   * Retire an outfit (DELETE): it leaves the gallery and the featured outfits but is kept, never deleted. The annex
   * revision is checked only when sent (a card loaded from a service before day outfits has none).
   */
  retireDesktopMascotOutfit: (id: string, expectedRevision: number, expectedAnnexRevision?: number) => {
    const query = new URLSearchParams({ expectedRevision: String(expectedRevision) });
    if (expectedAnnexRevision !== undefined) query.set('expectedAnnexRevision', String(expectedAnnexRevision));
    return http<DesktopMascotCatalog>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}?${query.toString()}`, { method: 'DELETE' });
  },
  /** A retired outfit, or one that never reached the gallery, back under its own id. */
  restoreDesktopMascotOutfit: (id: string, body: RestoreOutfitBody) =>
    http<DesktopMascotCatalog>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}/restore`, { method: 'POST', body: JSON.stringify(body) }),
  // Replace picture (lib/outfitPicture.ts): a new picture for a gallery outfit, in place; everything else about it stays.
  replaceDesktopMascotOutfitImage: (id: string, body: ReplacePictureBody) =>
    http<ReplacePictureAnswer>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}/image`, { method: 'PUT', body: JSON.stringify(body) }),
  // Which way outfits face (lib/outfitFacing.ts): the summary (admins and auditors), one batch of the backfill, and an
  // admin's own decision on one outfit (flip, undo, mark, judge again). Null from a service before facings (404).
  // The character reference (lib/mascotReference.ts): the hosted identity reference's slots, a replace and a rollback.
  desktopMascotReference: async (): Promise<ReferenceView | null> => {
    try {
      return await http<ReferenceView>('/v1/admin/desktop/mascot/reference');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  replaceDesktopMascotReference: (slot: ReferenceSlot, body: ReplaceReferenceBody) =>
    http<ReferenceView>(`/v1/admin/desktop/mascot/reference/${encodeURIComponent(slot)}`, { method: 'PUT', body: JSON.stringify(body) }),
  rollbackDesktopMascotReference: (slot: ReferenceSlot, body: RollbackReferenceBody) =>
    http<ReferenceView>(`/v1/admin/desktop/mascot/reference/${encodeURIComponent(slot)}/rollback`, { method: 'POST', body: JSON.stringify(body) }),

  desktopMascotFacingSummary: async (): Promise<FacingSummary | null> => {
    try {
      return await http<FacingSummary>('/v1/admin/desktop/mascot/facing/summary');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  runDesktopMascotFacingBackfill: (body: { cursor: string | null; limit?: number; scope?: BackfillScope }) =>
    http<BackfillAnswer>('/v1/admin/desktop/mascot/facing/backfill', { method: 'POST', body: JSON.stringify(body) }),
  setDesktopMascotOutfitFacing: (id: string, action: FacingAction) =>
    http<FacingActionAnswer>(`/v1/admin/desktop/mascot/outfits/${encodeURIComponent(id)}/facing`, { method: 'POST', body: JSON.stringify({ action }) }),
  /**
   * One outfit featured alone (every other one stops being featured), or none with null: the route from before several
   * outfits could be featured. The card uses the per-outfit toggle (updateDesktopMascotOutfit with `featured`) instead.
   */
  setDesktopMascotFeatured: (outfitId: string | null, expectedRevision: number) =>
    http<DesktopMascotCatalog>('/v1/admin/desktop/mascot/featured', { method: 'PUT', body: JSON.stringify({ outfitId, expectedRevision }) }),

  // Outfit categories (lib/outfitCategories.ts; owner, 2026-10-09): the registry, saved whole with the revision the
  // panel loaded; and these outfits into one category at once. An outfit's own category goes with
  // updateDesktopMascotOutfit (`category`).
  desktopMascotCategories: () => http<CategoriesView>('/v1/admin/desktop/mascot/categories'),
  saveDesktopMascotCategories: (body: SaveCategoriesBody) =>
    http<CategoriesView>('/v1/admin/desktop/mascot/categories', { method: 'PUT', body: JSON.stringify(body) }),
  assignDesktopMascotCategory: (body: AssignCategoryBody) =>
    http<AssignCategoryResult>('/v1/admin/desktop/mascot/categories/assign', { method: 'PUT', body: JSON.stringify(body) }),

  // Who always sees every outfit (lib/fullOutfitAccess.ts; owner, 2026-10-09): the e-mails and the group, saved whole with
  // the revision the section loaded. A 404 is a service from before it (or without the desktop app): null, and the
  // section hides.
  desktopMascotFullAccess: async (): Promise<FullAccessView | null> => {
    try {
      return await http<FullAccessView>('/v1/admin/desktop/mascot/full-access');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  saveDesktopMascotFullAccess: (body: SaveFullAccessBody) =>
    http<FullAccessView>('/v1/admin/desktop/mascot/full-access', { method: 'PUT', body: JSON.stringify(body) }),

  // Recognition celebrations (lib/celebrations.ts; docs/PLAN-recognition-celebration-2026-10-21.md section 4.5): the packs
  // the import tool registered and their switches, saved under the revision the section loaded. A 404 is a service with
  // the feature off (or without the desktop app): null, and the section hides.
  desktopMascotCelebrations: async (): Promise<CelebrationsView | null> => {
    try {
      return await http<CelebrationsView>('/v1/admin/desktop/mascot/celebrations');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  saveDesktopMascotCelebrations: (body: SaveCelebrationsBody) =>
    http<CelebrationsView>('/v1/admin/desktop/mascot/celebrations', { method: 'PUT', body: JSON.stringify(body) }),

  // The outfit economy (lib/outfitEconomy.ts; owner, 2026-10-15): rarity, supply, collections and packs, each write under
  // the revision the section loaded; the supply report. A 404 is a service with the economy off (or without the desktop
  // app): null, and the section hides.
  desktopMascotEconomy: async (): Promise<EconomyView | null> => {
    try {
      return await http<EconomyView>('/v1/admin/desktop/mascot/economy');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  desktopMascotEconomySupply: () => http<SupplyReport>('/v1/admin/desktop/mascot/economy/supply'),
  saveDesktopMascotEconomyItem: (id: string, body: SaveEconomyItemBody) =>
    http<EconomyView>(`/v1/admin/desktop/mascot/economy/items/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  saveDesktopMascotEconomyCollection: (id: string, body: SaveEconomyCollectionBody) =>
    http<EconomyView>(`/v1/admin/desktop/mascot/economy/collections/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  saveDesktopMascotEconomyPack: (id: string, body: SaveEconomyPackBody) =>
    http<EconomyView>(`/v1/admin/desktop/mascot/economy/packs/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),

  // Webhooks
  webhooks: () => http<{ webhooks: Webhook[] }>('/v1/admin/webhooks'),
  addWebhook: (patch: { url: string; secret?: string; events?: string[]; enabled?: boolean }) => http<{ webhooks: Webhook[] }>('/v1/admin/webhooks', { method: 'POST', body: JSON.stringify(patch) }),
  deleteWebhook: (id: string) => http<{ webhooks: Webhook[] }>(`/v1/admin/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // OAuth connectors (vault)
  connectors: () => http<{ connectors: Array<{ id: string; displayName: string; configured: boolean; connected: boolean }> }>('/v1/connectors'),
  revokeConnector: (id: string) => http<{ ok: boolean }>(`/v1/connectors/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: '{}' }),

  // Right-to-erasure (the answer, and its words: lib/governance.ts eraseResultText).
  eraseCapabilities: async () => {
    const value = await http<{ reviewRequired: boolean }>('/v1/admin/erase/capabilities');
    if (typeof value.reviewRequired !== 'boolean') throw new Error('The cleanup requirements could not be confirmed.');
    return value;
  },
  reviewBackgroundErasure: (userId: string) => http<BackgroundErasureReview>('/v1/admin/erase/background/review', { method: 'POST', body: JSON.stringify({ userId }) }),
  erase: (userId: string, request?: { requestId: string; background?: BackgroundErasureSubmission }) => http<EraseResult>(`/v1/admin/erase?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE', ...(request ? { headers: { 'Idempotency-Key': request.requestId } } : {}),
    ...(request?.background ? { body: JSON.stringify({ background: request.background }) } : {}),
  }),

  // OTA — exact release proposal -> independent approval -> durable publish
  otaProposals: () => http<{ proposals: OtaProposal[] }>('/v1/admin/ota/proposals'),
  otaPropose: (payload: OtaProposePayload) =>
    http<{ proposal: OtaProposal }>('/v1/admin/ota/proposals', { method: 'POST', body: JSON.stringify(payload) }),
  otaApprove: (proposalId: string) =>
    http<{ proposal: OtaProposal }>(`/v1/admin/ota/proposals/${encodeURIComponent(proposalId)}/approve`, { method: 'POST', body: '{}' }),
  otaPublish: (proposalId: string) =>
    http<OtaPublishResult>('/v1/admin/ota/publish', { method: 'POST', body: JSON.stringify({ proposalId }) }),

  // Operations: PC switches (lib/pcSwitches.ts; owner, 2026-10-21): the desktop agent's switches for everyone and per-person
  // exceptions, saved whole with the revision they were loaded at (409 policy_revision_conflict when another admin saved
  // first), and each PC's last report. Every route answers 404 pc_switches_off while the service has them switched off.
  desktopPolicy: () => http<DesktopPolicyView>('/v1/admin/desktop/policy'),
  saveDesktopPolicy: (body: DesktopPolicyBody) => http<DesktopPolicyView>('/v1/admin/desktop/policy', { method: 'PUT', body: JSON.stringify(body) }),
  desktopPolicyDevices: () => http<DevicePolicyRow[]>('/v1/admin/desktop/policy/devices'),
  // The people picker answers e-mails; an exception is keyed by object id (404 person_not_enrolled: no PC with Gyro yet).
  resolveDesktopPolicyPerson: (upn: string) => http<ResolvedPerson>(`/v1/admin/desktop/policy/resolve?upn=${encodeURIComponent(upn)}`),
  // The Early update circle's Entra groups (owner, 2026-10-27), saved whole with the revision they were loaded at (409
  // update_circles_changed when another admin saved first). Null from a service without the route (an older one).
  desktopUpdateCircles: async (): Promise<CirclesView | null> => {
    try {
      return await http<CirclesView>('/v1/admin/desktop/policy/circles');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404 && error.body?.error !== 'pc_switches_off') return null;
      throw error;
    }
  },
  saveDesktopUpdateCircles: (body: CirclesBody) => http<CirclesView>('/v1/admin/desktop/policy/circles', { method: 'PUT', body: JSON.stringify(body) }),

  // IT Support — remote diagnostic sessions
  supportDevices: () => http<{ devices: SupportDevice[]; authorizations: DeviceEnrollmentAuthorization[]; migrations: DeviceKeyMigration[] }>('/v1/admin/support/devices'),
  authorizeSupportDevice: (payload: { deviceId: string; publicKeyPem?: string; publicKeySha256?: string; purpose?: 'user' | 'broker-audit'; replacesPublicKeySha256?: string; machineName?: string; expiresInMs?: number }) =>
    http<{ ok: true; reused: boolean; authorization: DeviceEnrollmentAuthorization }>('/v1/admin/support/device-enrollment-authorizations', { method: 'POST', body: JSON.stringify(payload) }),
  revokeSupportDeviceAuthorization: (authorizationId: string) =>
    http<{ ok: boolean; reused: boolean }>(`/v1/admin/support/device-enrollment-authorizations/${encodeURIComponent(authorizationId)}`, { method: 'DELETE' }),
  revokeSupportDevice: (deviceId: string, reason: string) => http<{ ok: boolean; reused: boolean }>(`/v1/admin/support/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  revokeSupportDeviceKey: (deviceId: string, publicKeySha256: string, reason: string) =>
    http<{ ok: boolean; reused: boolean }>(`/v1/admin/support/devices/${encodeURIComponent(deviceId)}/keys/${encodeURIComponent(publicKeySha256)}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  supportSessions: () => http<{ sessions: SupportSessionSummary[] }>('/v1/admin/support/sessions'),
  createSupportSession: (payload: { requestId: string; reason: string; requestedMode: SupportSessionMode; jurisdiction: { country: string; region?: string } }) =>
    http<{ session: SupportSessionSummary; delivery: SupportDelivery }>('/v1/admin/support/sessions', { method: 'POST', body: JSON.stringify(payload) }),
  supportSession: (id: string, afterSeq = 0) =>
    http<SupportSessionDetail>(`/v1/admin/support/sessions/${encodeURIComponent(id)}${afterSeq > 0 ? `?afterSeq=${afterSeq}` : ''}`),
  notifySupportSession: (id: string) =>
    http<{ session: SupportSessionSummary; delivery: SupportDelivery }>(`/v1/admin/support/sessions/${encodeURIComponent(id)}/notify`, { method: 'POST', body: '{}' }),
  issueSupportCommand: (id: string, payload: { tool: string; args?: Record<string, unknown>; timeoutMs?: number }) =>
    http<{ command: SupportCommand; delivery: SupportDelivery }>(`/v1/admin/support/sessions/${encodeURIComponent(id)}/commands`, { method: 'POST', body: JSON.stringify(payload) }),
  supportRequests: () => http<{ requests: SupportRequest[] }>('/v1/admin/support/requests'),
  claimSupportRequest: (id: string) =>
    http<{ request: SupportRequest; session: { targetDeviceId: string; targetUserEmail?: string; reason: string } }>(
      `/v1/admin/support/requests/${encodeURIComponent(id)}/claim`,
      { method: 'POST', body: '{}' },
    ),
  resolveSupportRequest: (id: string) => http<{ request: SupportRequest }>(`/v1/admin/support/requests/${encodeURIComponent(id)}/resolve`, { method: 'POST', body: '{}' }),
  gateSupportDiagnosis: (id: string) =>
    http<{ diagnosis?: unknown; validation: { valid: boolean; errors: string[] }; gate: SupportGateResult }>(`/v1/admin/support/sessions/${encodeURIComponent(id)}/diagnosis/gate`, { method: 'POST', body: '{}' }),
  endSupportSession: (id: string) => http<{ session: SupportSessionSummary }>(`/v1/admin/support/sessions/${encodeURIComponent(id)}/end`, { method: 'POST', body: '{}' }),

  // Apps & access
  apps: () => http<{ apps: AppPolicy[] }>('/v1/admin/apps'),
  setApp: (key: string, patch: { enabled?: boolean; autonomy?: string | null; maxAutonomousRiskTier?: RiskTier; requireReversibleForAuto?: boolean; approvalRequiredAtOrAbove?: RiskTier | null; toolRoles?: Record<string, string[]>; webSearch?: WebSearchPolicyPatch | null }) =>
    http<{ apps: AppPolicy[] }>(`/v1/admin/apps/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(patch) }),

  // Voice
  voices: () => http<{ voices: Array<{ voiceId: string; name: string; category?: string }> }>('/v1/admin/voices'),
  voiceUsage: () => http<{ configured: boolean; usage: { tier: string | null; characterCount: number; characterLimit: number; remaining: number } | null }>('/v1/admin/voice-usage'),
  voiceLibrary: (opts: { search?: string; gender?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.search) p.set('search', opts.search);
    if (opts.gender) p.set('gender', opts.gender);
    const qs = p.toString();
    return http<{ voices: SharedVoice[]; error?: string; note?: string }>(`/v1/admin/voice-library${qs ? `?${qs}` : ''}`);
  },
  useLibraryVoice: (payload: { publicOwnerId: string; voiceId: string; name: string }) =>
    http<{ voiceId?: string; name?: string; ok?: boolean; message?: string }>('/v1/admin/voice-library/use', { method: 'POST', body: JSON.stringify(payload) }),
  libraryPreview: async (previewUrl: string): Promise<Blob | null> => {
    const res = await fetch(`/v1/admin/voice-library/preview?u=${encodeURIComponent(previewUrl)}`, { credentials: 'include' });
    return res.ok ? res.blob() : null;
  },
  ttsPreview: async (payload: { voiceId?: string; settings?: Partial<VoiceSettings>; text?: string; model?: string; effect?: VoiceEffect }): Promise<Blob | null> => {
    const res = await fetch('/v1/admin/tts/preview', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
    return res.ok ? res.blob() : null;
  },
};

export const playBlob = (blob: Blob): void => {
  try {
    const a = new Audio(URL.createObjectURL(blob));
    void a.play();
  } catch {
    /* ignore */
  }
};
